import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Bell, X, Check, Share } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { sfx } from "../services/soundfx";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}
function isiOS() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;
}

export default function InstallPrompt() {
  const { user } = useAuth();
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [notifState, setNotifState] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      localStorage.setItem("pq_installed", "1");
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Decide whether to show the popup once the parent is logged in.
  useEffect(() => {
    if (!user) return;
    if (isStandalone() || localStorage.getItem("pq_installed")) return;
    if (localStorage.getItem("pq_install_dismissed")) return;
    // show if we have a native prompt OR we're on iOS (manual instructions)
    const t = setTimeout(() => {
      if (deferred || isiOS()) setOpen(true);
    }, 1200);
    return () => clearTimeout(t);
  }, [user, deferred]);

  const dismiss = () => {
    sfx.tap();
    localStorage.setItem("pq_install_dismissed", "1");
    setOpen(false);
  };

  const install = async () => {
    sfx.unlock();
    if (!deferred) return; // iOS shows instructions instead
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice && choice.outcome === "accepted") {
      setInstalled(true);
      localStorage.setItem("pq_installed", "1");
    }
    setDeferred(null);
  };

  const enableNotifications = async () => {
    sfx.tap();
    if (typeof Notification === "undefined") {
      setNotifState("unsupported");
      return;
    }
    try {
      const perm = await Notification.requestPermission();
      setNotifState(perm);
      if (perm === "granted") {
        new Notification("Syllaberry 🦋", { body: "Yay! We'll remind you when it's reading time.", icon: "/icon-512.png" });
      }
    } catch (e) {
      /* ignore */
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-4" style={{ background: "#0F172A99", backdropFilter: "blur(6px)" }} data-testid="install-prompt">
        <motion.div
          initial={{ y: 60, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ type: "spring", stiffness: 240, damping: 22 }}
          className="relative w-full max-w-md rounded-[2rem] bg-white border-[6px] border-amber-300 shadow-2xl p-6 text-center"
        >
          <button data-testid="install-dismiss-btn" onClick={dismiss} className="absolute top-4 right-4 rounded-full bg-slate-100 p-2">
            <X className="w-5 h-5" />
          </button>

          <img src="/icon-512.png" alt="Syllaberry" className="w-20 h-20 rounded-2xl mx-auto mb-3 shadow-lg" />
          <h2 className="font-display text-2xl font-black text-amber-500 mb-1">Get the Syllaberry app!</h2>
          <p className="font-bold text-slate-500 text-sm mb-5">Add it to your device for 1-tap play — and it works offline too. 📱</p>

          {isiOS() && !deferred ? (
            <div className="rounded-2xl bg-sky-50 border-2 border-sky-200 p-4 text-left mb-4">
              <p className="font-bold text-sky-800 text-sm flex items-center gap-2 mb-1"><Share className="w-4 h-4" /> On iPhone/iPad:</p>
              <p className="text-sm text-slate-600 font-semibold">Tap the <b>Share</b> button, then <b>“Add to Home Screen”</b> to install Syllaberry.</p>
            </div>
          ) : (
            <button data-testid="install-app-btn" onClick={install} className="pop-btn w-full flex items-center justify-center gap-2 rounded-2xl bg-green-500 text-white font-display text-xl font-bold py-4 border-4 border-white shadow-[0_6px_0_#15803D] active:shadow-none mb-3">
              <Download strokeWidth={2.5} /> Install App
            </button>
          )}

          {/* Notifications */}
          <button
            data-testid="enable-notifications-btn"
            onClick={enableNotifications}
            disabled={notifState === "granted" || notifState === "unsupported"}
            className="pop-btn w-full flex items-center justify-center gap-2 rounded-2xl bg-sky-400 text-white font-display text-lg font-bold py-3 border-4 border-white shadow-[0_6px_0_#0284C7] active:shadow-none disabled:opacity-70"
          >
            {notifState === "granted" ? (<><Check strokeWidth={3} /> Reminders On</>) : notifState === "unsupported" ? ("Reminders not available") : (<><Bell strokeWidth={2.5} /> Turn On Reminders</>)}
          </button>

          <button data-testid="install-later-btn" onClick={dismiss} className="mt-3 font-bold text-slate-400 text-sm underline">
            Maybe later
          </button>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
